import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import RouteCard from "./RouteCard";

// Matches Diamond-PO-Prototype-v3.html's shipCard() exactly (lines 928-941).
// `shipment` is a JCDiamondShipment row (PascalCase, as the API returns it); FromJson/ToJson are
// parsed into RouteCard's shape (label/contact/line1/... -> Label/ContactName/Address/...).

const toRouteAddress = (snap) =>
  snap && {
    Label: snap.label,
    ContactName: snap.contact,
    Address: snap.line1,
    City: snap.city,
    State: snap.state,
    Zip: snap.zip,
    Country: snap.country,
    Phone: snap.phone,
  };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ShipmentCard = ({ shipment: s, docNumber, docKind, isRetailer, lineCount, onConfirmReceipt, onReceive }) => {
  const { t } = useTranslation();
  const out = s.Direction === "OUT";
  const pending = out ? !s.DeliveredUtc : !s.ReceivedUtc;
  const showConfirmReceipt = out && !s.DeliveredUtc && isRetailer && onConfirmReceipt;
  const showReceive = !out && !s.ReceivedUtc && !isRetailer && onReceive;

  return (
    <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div>
          <h3 style={{ margin: 0, display: "inline" }}>
            {out ? t("Outbound shipment") : t("Return shipment")} · {docNumber}
          </h3>{" "}
          {pending ? (
            <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#e8effc", color: "#2a52b8" }}>
              {t("In transit")}
            </span>
          ) : (
            <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#e3e7ef", color: "#1f2a44" }}>
              {out ? t("Delivered {{date}}", { date: fmtDate(s.DeliveredUtc) }) : t("Received {{date}}", { date: fmtDate(s.ReceivedUtc) })}
            </span>
          )}
          <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            {lineCount} {lineCount === 1 ? t("stone") : t("stones")} · {t("shipped")} {fmtDate(s.ShippedUtc)}
          </div>
        </div>
        {showConfirmReceipt && (
          <Button size="small" type="primary" onClick={() => onConfirmReceipt(s.ShipmentID)}>
            {t("Confirm receipt")}
          </Button>
        )}
        {showReceive && (
          <Button size="small" type="primary" onClick={() => onReceive(s.ShipmentID)}>
            {t("Mark received · relist")}
          </Button>
        )}
      </div>

      <RouteCard
        fromAddress={toRouteAddress(s.FromJson)}
        toAddress={toRouteAddress(s.ToJson)}
        fromPartyName={out ? s.SupplierName : s.RetailerName}
        toPartyName={out ? s.RetailerName : s.SupplierName}
      />

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12.5, marginTop: 12 }}>
        <span>
          <b style={{ display: "block", color: "#6b7689", fontSize: 10, textTransform: "uppercase" }}>{t("Carrier")}</b>
          {s.Carrier}
        </span>
        <span>
          <b style={{ display: "block", color: "#6b7689", fontSize: 10, textTransform: "uppercase" }}>{t("Tracking")}</b>
          <span style={{ fontFamily: "monospace" }}>{s.TrackingNo || "—"}</span>
        </span>
        <span>
          <b style={{ display: "block", color: "#6b7689", fontSize: 10, textTransform: "uppercase" }}>{t("Shipping cost")}</b>
          {fmtMoney(s.ShippingCost)} · {s.BillTo === "RETAILER" ? t("charged to retailer") : t("paid by supplier")}
          {out && s.BillTo === "RETAILER" && docKind === "MEMO" ? (s.InvoicedOnDocID ? ` · ${t("billed")}` : ` · ${t("not yet billed")}`) : ""}
        </span>
        <span>
          <b style={{ display: "block", color: "#6b7689", fontSize: 10, textTransform: "uppercase" }}>{t("Declared value")}</b>
          {fmtMoney(s.DeclaredValue)}
        </span>
        {out && docKind === "MEMO" && s.MarginType && !isRetailer && (
          <span>
            <b style={{ display: "block", color: "#6b7689", fontSize: 10, textTransform: "uppercase" }}>{t("Memo margin")}</b>
            {s.MarginType === "PERCENT" ? `${s.MarginValue}%` : `${fmtMoney(s.MarginValue)} / ${t("stone")}`}
            {s.ShowMarginToRetailer ? ` · ${t("shown to retailer")}` : ` · ${t("hidden from retailer")}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default ShipmentCard;
